import { useState } from 'react'
import { useSchoolStudents } from '@/hooks/useSchoolStudents'
import { useInstructors } from '@/hooks/useInstructors'
import { useSchoolStudentInvitations, type SchoolStudentIntakeDetails } from '@/hooks/useSchoolStudentInvitations'
import { useSchoolPackages } from '@/hooks/useSchoolPackages'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import { supabase } from '@/lib/supabase'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

function isMinor(dob: string): boolean {
  if (!dob) return false
  const birth = new Date(dob)
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  const beforeBirthdayThisYear =
    now.getMonth() < birth.getMonth() || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  if (beforeBirthdayThisYear) age -= 1
  return age < 18
}

const CREATE_ERROR_MESSAGES: Record<string, string> = {
  not_school_admin:          'You need to be an active owner or admin of this school to add a student.',
  school_id_required:        'Select which school this is for.',
  instructor_id_required:    'Choose which instructor this student is assigned to.',
  instructor_not_on_roster:  'That instructor is not on this school’s active roster.',
  invalid_email:             'Enter a valid student email address.',
  guardian_contact_required: 'This student is under 18 — guardian email and phone are both required.',
  internal_error:            'Something went wrong. Please try again.',
}

function AddStudentForm({ schoolId, onAdded }: { schoolId: string; onAdded: () => void }) {
  const { instructors } = useInstructors()
  const { creating, createInvitation } = useSchoolStudentInvitations()
  const { packages } = useSchoolPackages(schoolId)
  const activePackages = packages.filter(p => p.isActive)

  const [open, setOpen] = useState(false)
  const [instructorId, setInstructorId] = useState('')
  const [packageId, setPackageId] = useState('')
  const [studentEmail, setStudentEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [g1LicenceNumber, setG1LicenceNumber] = useState('')
  const [g1IssueDate, setG1IssueDate] = useState('')
  const [guardianName, setGuardianName] = useState('')
  const [guardianEmail, setGuardianEmail] = useState('')
  const [guardianPhone, setGuardianPhone] = useState('')
  const [homeAddress, setHomeAddress] = useState('')
  const [homeAddressPlaceId, setHomeAddressPlaceId] = useState<string | null>(null)
  const [pickupSameAsHome, setPickupSameAsHome] = useState(true)
  const [pickupAddress, setPickupAddress] = useState('')
  const [pickupAddressPlaceId, setPickupAddressPlaceId] = useState<string | null>(null)
  const [bdeCompletedDate, setBdeCompletedDate] = useState('')
  const [formError, setFormError] = useState('')
  const [notice, setNotice] = useState('')

  const minor = isMinor(dateOfBirth)

  function reset() {
    setInstructorId(''); setPackageId(''); setStudentEmail(''); setFullName(''); setDateOfBirth('')
    setG1LicenceNumber(''); setG1IssueDate('')
    setGuardianName(''); setGuardianEmail(''); setGuardianPhone('')
    setHomeAddress(''); setHomeAddressPlaceId(null)
    setPickupSameAsHome(true); setPickupAddress(''); setPickupAddressPlaceId(null)
    setBdeCompletedDate('')
  }

  async function handleSubmit() {
    setFormError(''); setNotice('')
    if (!instructorId) { setFormError('Choose which instructor this student is assigned to.'); return }
    if (!packageId) { setFormError('Choose a package for this student.'); return }
    if (!studentEmail.trim()) { setFormError('Student email is required.'); return }
    if (minor && (!guardianEmail.trim() || !guardianPhone.trim())) {
      setFormError('This student is under 18 — guardian email and phone are both required.')
      return
    }

    const instructor = instructors.find(i => i.userId === instructorId)
    const details: SchoolStudentIntakeDetails = {
      fullName: fullName.trim() || undefined,
      dateOfBirth: dateOfBirth || undefined,
      g1LicenceNumber: g1LicenceNumber.trim() || undefined,
      g1IssueDate: g1IssueDate || undefined,
      guardianName: guardianName.trim() || undefined,
      guardianEmail: guardianEmail.trim() || undefined,
      guardianPhone: guardianPhone.trim() || undefined,
      homeAddress: homeAddress.trim() || undefined,
      homeAddressPlaceId,
      pickupAddress: pickupSameAsHome ? undefined : (pickupAddress.trim() || undefined),
      pickupAddressPlaceId: pickupSameAsHome ? null : pickupAddressPlaceId,
      pickupSameAsHome,
      bdeCompletedDate: bdeCompletedDate || undefined,
    }

    const result = await createInvitation(schoolId, instructorId, instructor?.fullName ?? '', studentEmail.trim(), details, packageId || null)
    if (!result.ok) {
      setFormError(CREATE_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not add this student.')
      return
    }
    setNotice(result.emailed ? 'Invitation sent.' : 'Invitation created, but the email could not be sent — share the invite code with the student manually.')
    reset()
    onAdded()
  }

  const fieldLabelStyle: React.CSSProperties = {
    font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
  }

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 18 : 0 }}>
        <div style={{ font: 'var(--text-section)', color: 'var(--ink)' }}>Add a student</div>
        <button
          onClick={() => setOpen(v => !v)}
          style={{
            background: open ? 'rgba(27,42,74,0.06)' : '#0F7C6C', color: open ? '#1B2A4A' : '#fff',
            border: 'none', borderRadius: 8, padding: '9px 16px', font: '600 13px Inter, sans-serif', cursor: 'pointer',
          }}
        >
          {open ? 'Cancel' : '+ Add a student'}
        </button>
      </div>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Assigned instructor</div>
              <select value={instructorId} onChange={e => setInstructorId(e.target.value)} className='field-input' disabled={creating}>
                <option value=''>Select an instructor…</option>
                {instructors.map(i => (
                  <option key={i.userId} value={i.userId}>{i.fullName ?? i.email}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={fieldLabelStyle}>Student email</div>
              <input type='email' value={studentEmail} onChange={e => setStudentEmail(e.target.value)} placeholder='student@example.com' className='field-input' disabled={creating} />
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Package</div>
              <select value={packageId} onChange={e => setPackageId(e.target.value)} className='field-input' disabled={creating || activePackages.length === 0}>
                <option value=''>{activePackages.length === 0 ? 'No packages available — add one in Settings first' : 'Select a package…'}</option>
                {activePackages.map(p => (
                  <option key={p.id} value={p.id}>{p.name} — {p.inCarHours}h{p.price != null ? ` · $${p.price.toFixed(2)}` : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Full name</div>
              <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder='Student full name' className='field-input' disabled={creating} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Date of birth</div>
              <input type='date' value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} className='field-input' disabled={creating} />
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>G1 licence number</div>
              <input value={g1LicenceNumber} onChange={e => setG1LicenceNumber(e.target.value)} className='field-input' disabled={creating} />
            </div>
            <div>
              <div style={fieldLabelStyle}>G1 issue date</div>
              <input type='date' value={g1IssueDate} onChange={e => setG1IssueDate(e.target.value)} className='field-input' disabled={creating} />
            </div>
          </div>

          {minor && (
            <div style={{ background: 'rgba(212,151,43,0.08)', border: '1px solid rgba(212,151,43,0.25)', borderRadius: 10, padding: 16 }}>
              <div style={{ font: '600 12.5px Inter, sans-serif', color: '#9A6C0E', marginBottom: 12 }}>
                This student is under 18 — guardian contact is required
              </div>
              <div className='fg2' style={{ marginBottom: 14 }}>
                <div>
                  <div style={fieldLabelStyle}>Guardian name</div>
                  <input value={guardianName} onChange={e => setGuardianName(e.target.value)} className='field-input' disabled={creating} />
                </div>
                <div>
                  <div style={fieldLabelStyle}>Guardian email</div>
                  <input type='email' value={guardianEmail} onChange={e => setGuardianEmail(e.target.value)} className='field-input' disabled={creating} />
                </div>
              </div>
              <div style={{ maxWidth: 260 }}>
                <div style={fieldLabelStyle}>Guardian phone</div>
                <input value={guardianPhone} onChange={e => setGuardianPhone(e.target.value)} className='field-input' disabled={creating} />
              </div>
            </div>
          )}

          <AddressAutocomplete
            label='Home address'
            value={homeAddress}
            onChange={(addr, placeId) => { setHomeAddress(addr); setHomeAddressPlaceId(placeId) }}
            disabled={creating}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: '500 13px Inter, sans-serif', color: '#1B2A4A', cursor: 'pointer' }}>
            <input type='checkbox' checked={pickupSameAsHome} onChange={e => setPickupSameAsHome(e.target.checked)} disabled={creating} />
            Pickup address is the same as home address
          </label>

          {!pickupSameAsHome && (
            <AddressAutocomplete
              label='Pickup address'
              value={pickupAddress}
              onChange={(addr, placeId) => { setPickupAddress(addr); setPickupAddressPlaceId(placeId) }}
              disabled={creating}
            />
          )}

          <div style={{ maxWidth: 260 }}>
            <div style={fieldLabelStyle}>BDE course completion date</div>
            <input type='date' value={bdeCompletedDate} onChange={e => setBdeCompletedDate(e.target.value)} className='field-input' disabled={creating} />
          </div>

          {formError && <div className='banner-error'>{formError}</div>}
          {notice && (
            <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)', font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>
              {notice}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={creating}
            className='btn-teal'
            style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}
          >
            {creating ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      )}
    </div>
  )
}

function PackageCell({ relationshipId, packageId, packageName, schoolId, onChanged }: {
  relationshipId: string | null; packageId: string | null; packageName: string | null
  schoolId: string; onChanged: () => void
}) {
  const { packages } = useSchoolPackages(schoolId)
  const activePackages = packages.filter(p => p.isActive)
  const [saving, setSaving] = useState(false)

  if (!relationshipId) {
    return <span style={{ font: '400 13px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>—</span>
  }

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || null
    setSaving(true)
    try {
      await supabase.rpc('set_relationship_package', { p_relationship_id: relationshipId, p_package_id: value as string })
      onChanged()
    } finally {
      setSaving(false)
    }
  }

  return (
    <select
      value={packageId ?? ''}
      onChange={handleChange}
      disabled={saving}
      onClick={e => e.stopPropagation()}
      className='field-input'
      style={{ padding: '5px 8px', font: '400 12.5px Inter, sans-serif', width: 'auto', maxWidth: 200 }}
      title={packageName ?? undefined}
    >
      <option value=''>No package</option>
      {activePackages.map(p => (
        <option key={p.id} value={p.id}>{p.name} — {p.inCarHours}h</option>
      ))}
      {packageId && !activePackages.some(p => p.id === packageId) && packageName && (
        <option value={packageId}>{packageName} (archived)</option>
      )}
    </select>
  )
}

export default function Students() {
  const { students, loading, error, refresh } = useSchoolStudents()
  const { selectedSchoolId } = useSelectedSchool()

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
        Students
      </div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>
        Active students at your school
      </div>

      <SchoolSwitcher />

      {selectedSchoolId && <AddStudentForm schoolId={selectedSchoolId} onAdded={refresh} />}

      {loading && (
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>
          Loading…
        </div>
      )}

      {error && !loading && (
        <div className='banner-error' style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <span>{error}</span>
          <button
            onClick={refresh}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', flexShrink: 0 }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && students.length === 0 && (
        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 14, padding: '36px 28px', textAlign: 'center',
        }}>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>
            No students yet
          </div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            Students will appear here once they join your school.
          </div>
        </div>
      )}

      {!loading && !error && students.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Desktop header */}
          <div
            className='dt-head'
            style={{ gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,1.1fr) minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,0.9fr)' }}
          >
            <span>Student</span>
            <span>Instructor</span>
            <span>Package</span>
            <span>Completed</span>
            <span>Pending</span>
            <span>Joined</span>
          </div>

          {students.map((s, i) => {
            const isLast = i === students.length - 1
            return (
              <div key={s.studentId}>
                {/* Desktop row */}
                <div
                  className='dt-row-desk'
                  style={{
                    gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,1.1fr) minmax(0,1.3fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,0.9fr)',
                    borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)',
                  }}
                >
                  <div>
                    <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A' }}>
                      {s.fullName ?? '—'}
                    </div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                      {s.email}
                    </div>
                  </div>
                  <div style={{ font: '400 13px Inter, sans-serif', color: s.instructorName ? '#1B2A4A' : 'rgba(27,42,74,0.4)' }}>
                    {s.instructorName ?? '—'}
                  </div>
                  <div>
                    {selectedSchoolId && (
                      <PackageCell
                        relationshipId={s.relationshipId}
                        packageId={s.packageId}
                        packageName={s.packageName}
                        schoolId={selectedSchoolId}
                        onChanged={refresh}
                      />
                    )}
                  </div>
                  <div style={{ font: '600 13px Inter, sans-serif', color: s.lessonsCompleted > 0 ? '#0F7C6C' : 'rgba(27,42,74,0.3)' }}>
                    {s.lessonsCompleted}
                  </div>
                  <div style={{ font: '600 13px Inter, sans-serif', color: s.lessonsPending > 0 ? '#9A6C0E' : 'rgba(27,42,74,0.3)' }}>
                    {s.lessonsPending}
                  </div>
                  <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
                    {formatDate(s.joinedAt)}
                  </div>
                </div>

                {/* Mobile row */}
                <div
                  className='dt-row-mob'
                  style={{ borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.05)' }}
                >
                  <div>
                    <div style={{ font: '600 14px Inter, sans-serif', color: '#1B2A4A' }}>
                      {s.fullName ?? '—'}
                    </div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 2 }}>
                      {s.email}
                    </div>
                    {s.instructorName && (
                      <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                        {s.instructorName}
                      </div>
                    )}
                    {s.packageName && (
                      <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                        Package: {s.packageName}
                      </div>
                    )}
                    <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                      {s.lessonsCompleted} completed · {s.lessonsPending} pending
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && students.length > 0 && (
        <div style={{
          marginTop: 14, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)',
          textAlign: 'right',
        }}>
          {students.length} student{students.length !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  )
}
